import { apiClient } from './client';

export type SupportTicketStatus = 'waiting_support' | 'waiting_user' | 'closed';

export type SupportTicketCategory = 'bug' | 'question' | 'billing' | 'account' | 'other';

export type SupportTicketRequester = {
    id: string;
    full_name: string;
    email: string;
};

export type SupportTicketSummary = {
    id: string;
    user_id: string;
    category: SupportTicketCategory;
    subject: string;
    status: SupportTicketStatus;
    current_url: string | null;
    pii_acknowledged: boolean;
    closed_at: string | null;
    created_at: string;
    updated_at: string;
    requester?: SupportTicketRequester;
};

export type SupportTicketMessage = {
    id: string;
    ticket_id: string;
    author_type: 'user' | 'support';
    author_id: string;
    body: string;
    redacted_at: string | null;
    redacted_by: string | null;
    created_at: string;
};

export type SupportTechnicalContext = Record<string, string | number | boolean | null>;

export type SupportTicketDetail = SupportTicketSummary & {
    technical_context: SupportTechnicalContext | null;
    messages: SupportTicketMessage[];
};

export type SupportTicketListStatus = SupportTicketStatus | 'all';

export type CreateSupportTicketInput = {
    category: SupportTicketCategory;
    subject: string;
    body: string;
    current_url?: string | null;
    technical_context?: SupportTechnicalContext | null;
    pii_acknowledged: true;
};

type DataResponse<T> = {
    data: T;
};

const buildTicketListPath = (basePath: string, status?: SupportTicketListStatus) => {
    const params = new URLSearchParams();
    if (status && status !== 'all') {
        params.set('status', status);
    }

    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
};

export const supportApi = {
    getAgentStatus: async (): Promise<{ is_agent: boolean }> => {
        return (await apiClient.get('/support/agent/me')) as unknown as {
            is_agent: boolean;
        };
    },
    listTickets: async (
        status: SupportTicketListStatus = 'all',
    ): Promise<SupportTicketSummary[]> => {
        const response = (await apiClient.get<DataResponse<SupportTicketSummary[]>>(
            buildTicketListPath('/support/tickets', status),
        )) as unknown as DataResponse<SupportTicketSummary[]>;

        return response.data;
    },
    createTicket: async (input: CreateSupportTicketInput): Promise<SupportTicketDetail> => {
        const response = (await apiClient.post<DataResponse<SupportTicketDetail>>(
            '/support/tickets',
            input,
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    getTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
        const response = (await apiClient.get<DataResponse<SupportTicketDetail>>(
            `/support/tickets/${ticketId}`,
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    addTicketMessage: async (ticketId: string, body: string): Promise<SupportTicketDetail> => {
        const response = (await apiClient.post<DataResponse<SupportTicketDetail>>(
            `/support/tickets/${ticketId}/messages`,
            { body },
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    closeTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
        const response = (await apiClient.patch<DataResponse<SupportTicketDetail>>(
            `/support/tickets/${ticketId}/close`,
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    listAgentTickets: async (
        status: SupportTicketListStatus = 'all',
    ): Promise<SupportTicketSummary[]> => {
        const response = (await apiClient.get<DataResponse<SupportTicketSummary[]>>(
            buildTicketListPath('/support/agent/tickets', status),
        )) as unknown as DataResponse<SupportTicketSummary[]>;

        return response.data;
    },
    getAgentTicket: async (ticketId: string): Promise<SupportTicketDetail> => {
        const response = (await apiClient.get<DataResponse<SupportTicketDetail>>(
            `/support/agent/tickets/${ticketId}`,
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    addAgentTicketMessage: async (ticketId: string, body: string): Promise<SupportTicketDetail> => {
        const response = (await apiClient.post<DataResponse<SupportTicketDetail>>(
            `/support/agent/tickets/${ticketId}/messages`,
            { body },
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    updateAgentTicketStatus: async (
        ticketId: string,
        status: SupportTicketStatus,
    ): Promise<SupportTicketDetail> => {
        const response = (await apiClient.patch<DataResponse<SupportTicketDetail>>(
            `/support/agent/tickets/${ticketId}/status`,
            { status },
        )) as unknown as DataResponse<SupportTicketDetail>;

        return response.data;
    },
    redactMessage: async (messageId: string): Promise<SupportTicketMessage> => {
        const response = (await apiClient.patch<DataResponse<SupportTicketMessage>>(
            `/support/agent/messages/${messageId}/redact`,
        )) as unknown as DataResponse<SupportTicketMessage>;

        return response.data;
    },
};
